import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import CONSTANTS, { PAGES_ROUTES } from '../Constants'
import { API_LIST } from '../Constants/ApiDefinations'
import { getLocalStorage } from '../Helpers/Storage'
import { getParsedToken, logoutCyberArk } from '../Helpers/Util'
import { store } from '../Store'
import { showAlert } from '../Store/Slice/alert.slice'
import { refreshTokenSafely } from './tokenManager'

axios.interceptors.request.use(
  async (config) => {
    const initialHeader = config.headers['Content-Type']
    const configCopy: any = { ...config }
    configCopy.headers['Content-Type'] = initialHeader || 'application/json'
    const parsedToken: any = getParsedToken()
    if (parsedToken?.sub?.sessionId) {
      configCopy.headers['device_id'] = `${parsedToken.sub.sessionId}${
        parsedToken?.exp ? '-' + parsedToken.exp : ''
      }`
      configCopy.headers['session_id'] =
        `${parsedToken.sub.sessionId}-${window?.navigator?.userAgent}`
    }
    if (!configCopy.headers['Authorization'] && !config.headers['upload']) {
      const { token = '' } = getLocalStorage('auth_config') as any
      if (token) configCopy.headers['Authorization'] = ` bearer ${token}`
    }

    const toSendTransactionId = CONSTANTS.TRANSACTION_APIS.find(
      (item: any) =>
        item.method === configCopy.method &&
        CONSTANTS.TRANSACTION_APIS.some((data: any) => configCopy.url.includes(data.url)),
    )

    if (toSendTransactionId) {
      const transId = localStorage.getItem(CONSTANTS.LOCAL_KEYS.TRANSACTION_ID)
      if (transId) {
        configCopy.headers['metacategoryTxnId'] = await transId
      }
    }

    return configCopy
  },
  (error) => Promise.reject(error),
)

axios.interceptors.response.use(
  function (response) {
    return response?.data
  },
  function (error) {
    switch (error?.response?.status) {
      case 401:
        logoutCyberArk(error?.response?.data?.message)
        break
      case 500:
        if (error?.config?.url?.includes('auth/refreshToken')) {
          window.location.href = PAGES_ROUTES.LoginPage
        } else if (!error?.config?.url?.includes('content/event-notifications/action/poll')) {
          store.dispatch(showAlert([true, 'Internal Server Error', 'error']))
          window.location.href = PAGES_ROUTES.ErrorPage
        }
        break
    }
    if (error?.response?.data) throw error.response.data
    throw error
  },
)

const mainApiService = async ({
  baseUrl,
  url,
  method,
  data,
  cancelToken,
  params,
  headers,
  withoutBaseURL = false,
  signal,
  version,
  versionReplaceFrom,
  isNewConfig = false,
}: {
  baseUrl?: string | undefined
  url: string
  method: AxiosRequestConfig['method']
  data?: AxiosRequestConfig['data']
  params?: AxiosRequestConfig['params']
  headers?: AxiosRequestConfig['headers']
  withoutBaseURL?: boolean
  signal?: any
  cancelToken?: any
  version?: any
  versionReplaceFrom?: string
  isNewConfig?: boolean
}): Promise<{
  data?: AxiosRequestConfig['data']
  error?: any
  responseCode?: any
  message?: any
  errorMessage?: string[]
}> => {
  try {
    let baseApiUrl = CONSTANTS.BASE_URL
    if (isNewConfig) {
      baseApiUrl = baseApiUrl.replace('/api/v1/service', '/api')
    }
    const bUrl = baseUrl ?? baseApiUrl

    // Skip token refresh check for refresh token endpoint itself to avoid recursion
    const tokenRefreshEndpoint = API_LIST.AUTH.REFRESH_TOKEN
    if (!url.includes(tokenRefreshEndpoint.url)) {
      // Check if token needs refresh and handle concurrent calls
      const { expireTime = '' } = getLocalStorage('auth_config')
      const refreshToken = getLocalStorage('refresh_token')
      if (expireTime && expireTime < Date.now() && refreshToken === true) {
        try {
          // Wait for token refresh to complete (handles concurrent calls)
          await refreshTokenSafely(
            baseApiUrl.replace('/api/v1/service', '/api') + tokenRefreshEndpoint.url,
          )
        } catch (refreshError) {
          // If refresh fails, don't proceed with the original API call
          // The refreshTokenSafely function will handle logout for 401 errors
          console.error('Token refresh failed:', refreshError)
          return {
            error: refreshError,
            errorMessage: ['Authentication failed. Please login again.'],
          }
        }
      }
    }

    let apiURL: string = bUrl + url
    if (withoutBaseURL) {
      apiURL = url
    }
    if (version) {
      apiURL = apiURL.replace(`${versionReplaceFrom || 'v1'}`, version)
    }
    const result = await axios({
      url: apiURL,
      cancelToken,
      method,
      data,
      params,
      headers,
      ...(signal ? { signal } : {}),
    })
    return result as AxiosResponse
  } catch (axiosError) {
    const err = axiosError as any
    const errorMessage = err?.response?.data?.errors || []
    return { error: err, errorMessage }
  }
}

export default mainApiService
